import React, { useState, useEffect } from 'react';
import { FaInstagram, FaTimes, FaInfoCircle, FaBullhorn, FaTrophy, FaUsers, FaExclamationTriangle, FaUserPlus } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import '../css/HeaderMessage.css';
import { authenticatedApiCall } from '../utils/authenticatedApiCall';

const HeaderMessage = ({ isLoggedIn, username, onLoadingChange }) => {
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [closingNotification, setClosingNotification] = useState(null);

    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const fetchNotifications = async () => {
            if (onLoadingChange) {
                onLoadingChange(true);
            }

            try {
                const response = await authenticatedApiCall('/current-notifications', 'GET');
                if (isMounted && Array.isArray(response)) {
                    setNotifications(response);
                    setShowNotifications(response.length > 0);
                } else {
                    setNotifications([]);
                    setShowNotifications(false);
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
            } finally {
                if (isMounted) {
                    timeoutId = setTimeout(fetchNotifications, 1800000); // 30 minutes
                    if (onLoadingChange) {
                        onLoadingChange(false);
                    }
                }
            }
        };

        if (isLoggedIn) {
            fetchNotifications();
        }

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isLoggedIn, onLoadingChange]);


    const handleClose = (notificationId) => {
        if (closingNotification) return;

        setClosingNotification(notificationId);

        setTimeout(() => {
            setNotifications(prevNotifications =>
                prevNotifications.filter(notification => notification.id !== notificationId)
            );

            setShowNotifications(notifications.length > 1);

            authenticatedApiCall(`/notifications/${notificationId}/update`, 'POST', { action: 'close' })
                .catch(error => console.error('Error marking notification as closed:', error));

            setClosingNotification(null);
        }, 300); // Match this with the animation duration
    };

    const handleActionClick = (notificationId, actionUrl) => {
        authenticatedApiCall(`/notifications/${notificationId}/update`, 'POST', { action: 'open' })
            .catch(error => console.error('Error marking notification as opened:', error));
        window.open(actionUrl, '_blank');
    };

    const getIcon = (type) => {
        switch (type) {
            case 'instagram': return FaInstagram;
            case 'info': return FaInfoCircle;
            case 'promotion': return FaBullhorn;
            case 'contest': return FaTrophy;
            case 'community': return FaUsers;
            case 'warning': return FaExclamationTriangle;
            case 'follow': return FaUserPlus;
            default: return FaInfoCircle;
        }
    };

    if (!isLoggedIn || !showNotifications || notifications.length === 0) return null;

    return (
        <AnimatePresence>
            {showNotifications && (
                <motion.div
                    className="header-message-stack"
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.3 }}
                >
                    {notifications.map((notification, index) => {
                        const Icon = getIcon(notification.type);
                        return (
                            <motion.div
                                key={notification.id}
                                className={`header-message ${notification.type}`}
                                initial={{ opacity: 0, y: -20, zIndex: notifications.length - index }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    scale: closingNotification === notification.id ? 0.9 : 1,
                                }}
                                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                                transition={{ duration: 0.2 }}
                                style={{
                                    position: 'absolute',
                                    top: `${index * 10}px`,
                                    right: 0,
                                    left: 0,
                                }}
                            >
                                <button
                                    className="hm-close-button"
                                    onClick={() => handleClose(notification.id)}
                                    disabled={closingNotification !== null}
                                >
                                    <FaTimes />
                                </button>
                                <div className="hm-icon">
                                    <Icon />
                                </div>
                                <div className="hm-content">
                                    <h3 className="hm-title">{notification.title}</h3>
                                    <p className="hm-description">{notification.message}</p>
                                </div>
                                {notification.action && (
                                    <button
                                        className={`hm-button 
                                        ${notification.type === 'instagram' ? 'hm-instagram-button' : ''}
                                        ${notification.type === 'promotion' ? 'hm-promotion-button' : ''}
                                        ${notification.type === 'contest' ? 'hm-contest-button' : ''}
                                        ${notification.type === 'community' ? 'hm-community-button' : ''}
                                        ${notification.type === 'warning' ? 'hm-warning-button' : ''}
                                        ${notification.type === 'follow' ? 'hm-follow-button' : ''}
                                    `}
                                        onClick={() => handleActionClick(notification.id, notification.action)}
                                    >
                                        {notification.type === 'instagram' && 'Open Instagram'}
                                        {notification.type === 'promotion' && 'Check Promotion'}
                                        {notification.type === 'contest' && 'Join Contest'}
                                        {notification.type === 'community' && 'Join Community'}
                                        {notification.type === 'warning' && 'Learn More'}
                                        {notification.type === 'follow' && 'View Profile'}
                                        {notification.type === 'info' && 'Learn More'}
                                        {notification.type !== 'instagram' && notification.type !== 'promotion' &&
                                            notification.type !== 'contest' && notification.type !== 'community' &&
                                            notification.type !== 'warning' && notification.type !== 'follow' &&
                                            notification.type !== 'info' && 'Learn More'}
                                    </button>

                                )}
                            </motion.div>
                        );
                    })}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default HeaderMessage;
